// What Cole is told when the work originates outside the suggestion box.
//
// Same doctrine as suggestion-decision.ts's briefFor: the brief is BUILT from
// the record, never typed by anybody — a human writing an engineering brief by
// hand is the nine-act ceremony this pipeline exists to delete. And every
// brief carries the same two standing instructions: make the smallest correct
// version, and "it already works / it is not a code problem" is a real answer.

import { bugReference } from "../../bug-reference";

export interface BugFacts {
  /** The report's id. When present, the brief carries its BUG-XXXXXXXX reference. */
  id?: string | null;
  title: string | null;
  description: string | null;
  page_url: string | null;
  severity: string | null;
  user_agent: string | null;
}

export interface DevRequestBriefFacts {
  title: string;
  content: string;
  app: string | null;
  area: string | null;
  admin_response: string | null;
}

/** One line an admin recognises in the assignment list. */
export function assignmentTitleFor(kind: "bug" | "dev_request", title: string | null): string {
  const t = (title ?? "").trim().split("\n")[0].slice(0, 180) || "(untitled)";
  return kind === "bug" ? `Bug: ${t}` : `Dev request: ${t}`;
}

/**
 * A bug brief is an INVESTIGATION brief first. A reporter describes a symptom,
 * not a cause — the page they were on and what they saw is evidence, and the
 * job starts with reproducing it, not with typing a fix for the words.
 */
export function briefForBug(bug: BugFacts): string {
  return [
    "Somebody at Scenta hit a bug and reported it:",
    "",
    `WHAT THEY SAW: ${(bug.title ?? "").trim() || "(no title)"}`,
    (bug.description ?? "").trim() || "(no further description)",
    "",
    bug.page_url ? `WHERE: ${bug.page_url}` : null,
    bug.severity ? `HOW BAD THEY RATED IT: ${bug.severity}` : null,
    bug.user_agent ? `THEIR BROWSER: ${bug.user_agent}` : null,
    bug.id ? `REFERENCE: ${bugReference(bug.id)}` : null,
    "",
    "A report describes a SYMPTOM, not a cause. Start by finding the code path",
    "behind what they saw and explaining the mechanism — then make the smallest",
    "correct fix. If the behaviour is actually correct, or the problem is data",
    "or configuration rather than code, say so plainly and change nothing;",
    "that is a real resolution and it goes back to whoever reported it.",
    bug.id ? "" : null,
    // HQ-BUG-TRACE-001: the one thread from the report to the change. A commit or
    // PR that names it can be found from the bug, and the bug from the change.
    bug.id ? `Put ${bugReference(bug.id)} in the pull request title and in the commit message.` : null,
  ].filter(v => v !== null).join("\n");
}

/**
 * A dev-request brief is a BUILD brief: the asker described a job they cannot
 * do, an admin committed to it, and what ships should be the smallest thing
 * that genuinely unblocks that job.
 */
export function briefForDevRequest(r: DevRequestBriefFacts): string {
  return [
    "Somebody at Scenta needs functionality that does not exist yet, and it has",
    "been approved for building:",
    "",
    `THE ASK: ${r.title.trim()}`,
    "",
    r.content.trim(),
    "",
    r.app ? `WHICH APPLICATION: ${r.app}` : null,
    r.area ? `COMPANY FUNCTION SERVED: ${r.area}` : null,
    r.admin_response?.trim() ? `THE ADMIN'S NOTE WHEN COMMITTING: "${r.admin_response.trim()}"` : null,
    "",
    "Work out the smallest correct version that genuinely unblocks the job they",
    "described — not the grandest reading of the words. If HQ already does this,",
    "say so plainly, point at where, and change nothing.",
  ].filter(v => v !== null).join("\n");
}

/**
 * What an assignment's status means to somebody looking at the bug or request
 * it came from. Business words; an unknown status shows itself rather than
 * hiding behind a guess.
 */
export function workStageLabel(status: string | null | undefined): string {
  switch (status) {
    case "approved_to_investigate": return "Cole: queued";
    case "investigating": return "Cole: investigating";
    case "investigated": return "Cole: has a question";
    case "approved": return "Cole: building";
    case "working": case "in_progress": return "Cole: building";
    case "changes_ready": return "Cole: PR ready";
    case "proposed": return "Cole: proposed";
    case "merged": return "Cole: shipped";
    case "no_change_needed": return "Cole: no change needed";
    case "failed": return "Cole: failed";
    case "abandoned": return "Cole: abandoned";
    default: return status ? `Cole: ${status}` : "";
  }
}

/** Statuses where the work CONCLUDED — nothing is running, nothing is waiting.
 * These are the states a clarification can reopen; open work is answered
 * through the existing question box instead of forked. */
export const CONCLUDED_STATUSES: readonly string[] = [
  "no_change_needed", "merged", "failed", "abandoned",
];

/**
 * The brief for a SECOND pass, after a person said "that's not what they meant".
 *
 * ── WHY THE EARLIER CONCLUSION RIDES ALONG ────────────────────────────────
 * Cole's first answer was usually correct FOR THE WORDS — "emoji already
 * send fine" is true, and the asker meant an emoji PICKER. Hiding the first
 * conclusion invites him to walk the same path to the same answer; carrying it
 * and then contradicting it with the clarification is what actually redirects
 * the work. The clarification is placed last and marked authoritative: where
 * it disagrees with the original wording, the clarification wins.
 */
export function clarifiedBrief(args: {
  originalInstructions: string;
  priorFinding: string | null;
  clarification: string;
}): string {
  return [
    args.originalInstructions.trim(),
    "",
    "── YOU ALREADY LOOKED AT THIS ONCE ──",
    args.priorFinding?.trim()
      ? `You concluded: ${args.priorFinding.trim()}`
      : "Your earlier attempt concluded without shipping a change.",
    "",
    "── WHAT THEY ACTUALLY MEANT ──",
    args.clarification.trim(),
    "",
    "The clarification above is authoritative: where it disagrees with the",
    "original wording or with your earlier conclusion, the clarification wins.",
    "Start from it, not from your earlier answer, and build the smallest",
    "correct version of what it describes.",
  ].join("\n");
}
